import { objectSpread2 as _objectSpread2, asyncToGenerator as _asyncToGenerator } from '../_virtual/_rollupPluginBabelHelpers.js';
import DataLoader from 'dataloader';
import { Type, fetchTranslations } from './source.js';
import { LoaderChannel } from './loader-channel.js';
import { TranslationCache } from './translation-cache.js';
import { parse } from './parse.js';
import { stringify } from './stringify.js';

var COMPONENT_KEYS = Symbol("Component’s translation keys");
/**
 * Data loader for batch loading and caching translations
 *
 * @example // Minimal example
 * const loader = new Herz.I18n.Loader({
 *   url: 'https://clients.example.com/translations/search',
 * })
 *
 * @example // With key prefixing
 * const loader = new Herz.I18n.Loader({
 *   transformAlias(alias) {
 *     return `boom.${alias}`
 *   },
 *   url: 'https://clients.boomcasino.com/translations/search',
 * })
 *
 * @param {!Object} options
 * @param {("babelfisk"|"casinosaga")} options.backend Translation backend
 * @param {?Function} options.transformAlias Key normalizer; receives each
 * key that is being used. Passes value through by default.
 * @param {!string} options.url Full URL for endpoint to fetch translations
 * from
 *
 * @access public
 */

class Loader extends DataLoader {
  constructor(_ref) {
    var {
      provider = Type.CASINOSAGA,
      transformAlias,
      url
    } = _ref;
    super( /*#__PURE__*/function () {
      var _batchLoadTranslations = _asyncToGenerator(function* (keys) {
        try {
          var translations = yield fetchTranslations(provider, this.url, {
            keys,
            language: this.language,
            bucket: this.bucket
          });
          return keys.map(key => parse(translations[key]));
        } catch (error) {
          return keys.map(() => error);
        }
      });

      function batchLoadTranslations(_x) {
        return _batchLoadTranslations.apply(this, arguments);
      }

      return batchLoadTranslations;
    }(), {
      batchScheduleFn: callback => setTimeout(callback, 100),
      cacheMap: new TranslationCache()
    });
    this.transformAlias = transformAlias || this.transformAlias;
    this.url = url;
    this.provider = provider; // Return only bound public methods to remove possible usage of translation
    // loader’s implementation details

    return {
      extractCache: this.extractCache.bind(this),
      init: this.init.bind(this),
      getTranslate: this.getTranslate.bind(this),
      loadMany: this.loadMany.bind(this),
      restoreCache: this.restoreCache.bind(this),
      visit: this.visit.bind(this)
    };
  }
  /**
   * Create `translate` method
   *
   * Optionally prefetches given keys
   *
   * !NB: This is internal method used by `useTranslate` hook and is not
   * supposed to be used directly. If you come across use case for it, consult
   * with project owners.
   *
   * @param {string} aliases
   * @returns {Function} `translate` method
   *
   * @access private
   */


  getTranslate(aliases) {
    if (aliases) {
      this.loadMany(aliases);
    }

    return (alias, data) => {
      var cacheKey = this.transformAlias(alias);

      var value = this._cacheMap.read(cacheKey);

      if (value == null) {
        return null;
      }

      return stringify(this.processors, _objectSpread2(_objectSpread2({}, this.globals), data), value);
    };
  }
  /**
   * Initialise loader with i18n configuration
   *
   * !NB: This is internal method used by `TranslateProvider` and is not
   * supposed to be used directly. If you come across use case for it, consult
   * with project owners.
   *
   * @param {Object} options
   * @param {Object} options.globals Global variables and their values
   * @param {string} options.language Language to fetch content in
   * @param {Object} options.processors Processor definitions
   *
   * @access private
   */


  init(_ref2) {
    var {
      globals,
      language,
      bucket,
      processors
    } = _ref2;

    if (globals == null || typeof globals !== "object") {
      throw new TypeError('"globals" must be an object');
    }

    this.globals = globals;

    if (language == null) {
      throw new TypeError('"language" is required');
    }

    if (this.provider === Type.CASINOSAGA && typeof language !== "string") {
      throw new TypeError('"language" must be a string');
    }

    if (this.provider === Type.BABELFISK) {
      if (!Array.isArray(language) || language.some(item => typeof item !== "string")) {
        throw new TypeError('"language" must be an array of strings');
      }

      if (language.length === 0) {
        throw new TypeError('"language" must have at least one (1) language');
      }
    }

    this.language = language;
    this.bucket = bucket;

    if (processors == null || typeof processors !== "object") {
      throw new TypeError('"processors" must be an object');
    }

    for (var name in processors) {
      var processor = processors[name];

      if (typeof processor !== "function") {
        throw new TypeError("\"processors.".concat(processor, "\" must be a function"));
      }
    }

    this.processors = processors;

    if (process.browser) {
      LoaderChannel.subscribe(this.loadMany.bind(this));
    }
  }
  /**
   * Enqueue multiple translations fetching.
   *
   * This is a low-level API that you might not need unless direct (outside of
   * React) integration with translation loader is required.
   *
   * @param {Array<string>} alias Unprefixed translation keys
   * @returns {Promise<Array<string|null>>} Unparsed translation strings
   *
   * @access public
   */


  loadMany(aliases) {
    var _superprop_getLoadMany = () => super.loadMany,
        _this = this;

    return _asyncToGenerator(function* () {
      return _superprop_getLoadMany().call(_this, aliases.map(alias => _this.transformAlias(alias)));
    })();
  }
  /**
   * Transform alias into key
   *
   * Override this method if you need to add prefix or perform other kind of
   * normalisation.
   * This method can be overridden via constructor; no transforms are performed
   * in default implementation.
   *
   * @param {string} alias
   *   Key alias (external key that might differ from final key stored in DB)
   * @returns {string} Transformed key
   *
   * @access private
   */


  transformAlias(alias) {
    return alias;
  }
  /**
   * Attempt to read component translation keys and preload them if available
   *
   * It is used to preload keys on server-side instead of {@link LoaderChannel}
   * for two reasons.
   *
   * Concurrency. Many incoming requests make global state unsafe as it is
   * being shared and translation data for different users might get corrupted.
   *
   * The nature of JavaScript modules. Each module is evaluated only once when
   * imported and then cached by NodeJS thus {@link Loader.preload} will not be
   * executed for each new request as it is called on module evaluation.
   *
   * This a visitor to be used with a tool that walks React component tree,
   * such as `react-walk-tree` or `react-ssr-prepass`.
   *
   * @example // With react-ssr-prepass
   * await prepass(<App />, (element, instance) => {
   *   loader.visit(element)
   * })
   *
   * @param {Object} element React element
   *
   * @access public
   */


  visit(element) {
    if (element.type[COMPONENT_KEYS]) {
      return this.loadMany(element.type[COMPONENT_KEYS]);
    }
  }
  /**
   * Get serializeable cache object
   *
   * Main use case is to transfer translation cache from server to client for
   * further hydration by passing serialized cache to
   * {@link TranslationLoader#restoreCache}
   *
   * @example // Injecting into HTML
   * <script
   *   dangerouslySetInnerHTML={{
   *     __html: `window.__I18N_DATA__ = ${stringify(loader.extractCache())}`,
   *   }}
   * />
   *
   * @returns {Object}
   *
   * @access public
   */


  extractCache() {
    var cache = {};

    for (var [key, value] of this._cacheMap) {
      cache[key] = value.read();
    }

    return cache;
  }
  /**
   * Prime TranslationLoader instance cache
   *
   * Main use case is app hydration where you pass cache extracted with
   * {@link TranslationLoader#extractCache} and transferred from server to
   * client.
   *
   * @example // Reading from global
   * loader.restoreCache(window.__I18N_DATA__)
   *   .then(() => {
   *     // render the application
   *   })
   *
   * @async
   * @param {!Object} cache Serialized TranslationLoader cache
   *
   * @returns {Promise<undefined>}
   *
   * @access public
   */


  restoreCache(cache) {
    for (var key in cache) {
      var value = cache[key];
      this.prime(key, value);
    } // Allow `await`ing. This is needed due to how cache priming works. Each
    // value is wrapped with data resolver (see `TranslationCache` for more
    // details) and is not readable in current tick.


    return Promise.resolve();
  }
  /**
   * Mark component to prefetch given keys ahead of time.
   *
   * Used for static keys (i.e. independent from runtime conditions) to make use
   * of Render-as-You-Fetch approach.
   *
   * For keys which names depend on variables or which must be fetched only if
   * certain condition is met use `useTranslate` hook
   *
   * @example
   * function Footer() {
   *   const translate = Herz.I18n.useTranslate()
   *
   *   // ...
   * }
   *
   * Herz.I18n.Loader.preload(["footer.copyright", "footer.support"], Footer)
   *
   * @see https://reactjs.org/docs/concurrent-mode-suspense.html
   *
   * @param {Array.<string>} keys
   * @param {React.Component} component
   *
   * @access public
   */


  static preload(keys, component) {
    if (process.browser) {
      // Use channel to communicate with loader. See `LoaderChannel`
      // implementation for more information
      LoaderChannel.enqueue(keys);
    } else {
      // Attach keys to compoent instance; they will be used to preload keys
      // during prepass phase. See `TranslationLoader#visit` for more
      // information
      component[COMPONENT_KEYS] = keys;
    }
  }

  static resetChannel() {
    LoaderChannel.destroyChannel();
  }

}

export { Loader };
